import { useEffect, useRef, useState } from "react";
import {
  Attribute,
  AttributeGroup,
  AttributeProperty,
  ComponentProps,
  ConfigsProvider,
} from "react-builder";
import styled from "styled-components";

@AttributeGroup({
  title: "CountdownTimerProvider",
  blockName: "CountdownTimerProvider",
  icon: "countdown",
})
export class CountdownTimerProviderAttributes extends Attribute {
  @AttributeProperty({ type: "Number" })
  initialSecondOffset: number = 0;
}

const CountdownTimerProviderTheme = styled.div(
  (props: { attributes: CountdownTimerProviderAttributes }) => `

    ${props.attributes.toCSS()}

`
);

export function CountdownTimerProvider(props: ComponentProps<CountdownTimerProviderAttributes>) {
  const initialSecondOffset = props.attributes.initialSecondOffset;
  const [currentSecond, setCurrentSecond] = useState<number>(initialSecondOffset);
  const [formatted, setFormatted] = useState({
    days: "0",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  useEffect(() => {
    startInterval();

    return () => {
      stopInterval();
    };
  }, []);

  useEffect(() => {
    setCurrentSecond(initialSecondOffset);
  }, [initialSecondOffset]);

  useEffect(() => {
    const days = Math.floor(currentSecond / 86400);
    const hours = Math.floor((currentSecond % 86400) / 3600);
    const minutes = Math.floor((currentSecond % 3600) / 60);
    const seconds = Math.floor(currentSecond % 60);

    setFormatted({
      days: `${days}`,
      hours: hours > 9 ? `${hours}` : `0 ${hours}`,
      minutes: minutes > 9 ? `${minutes}` : `0 ${minutes}`,
      seconds: seconds > 9 ? `${seconds}` : `0 ${seconds}`,
    });
  }, [currentSecond]);

  const decrementTimer = () => {
    setCurrentSecond((prevSecond) => {
      if (prevSecond > 0) {
        return Math.max(0, prevSecond - 1);
      } else {
        stopInterval();
        return 0;
      }
    });
  };

  const startInterval = () => {
    stopInterval();
    intervalRef.current = setInterval(() => {
      decrementTimer();
    }, 1000);
  };

  const stopInterval = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const reset = () => {
    stopInterval();
    setCurrentSecond(initialSecondOffset);
    startInterval();
  };

  const content = {
    days: formatted.days,
    hours: formatted.hours,
    minutes: formatted.minutes,
    seconds: formatted.seconds,
    reset: () => {
      reset();
    },
    stop: () => {
      stopInterval();
    },
    continue: () => {
      startInterval();
    },
  };

  return (
    <ConfigsProvider configJson={{ countdownTimer: content }}>
      <CountdownTimerProviderTheme {...props} attributes={props.attributes}>
        {props.children}
      </CountdownTimerProviderTheme>
    </ConfigsProvider>
  );
}

CountdownTimerProvider.prototype.Props = CountdownTimerProviderAttributes;

export default CountdownTimerProvider;
